// Quick-start (architecture sprint-04 §6.1 F47; sprint 7 §7.1). "Start" always sends the suggested
// project or an explicit null, so the default project is never applied (A-12).
import type { LocalDate } from '@shared/calendar';
import { X } from 'lucide-react';
import { ColorSwatch } from '@/components/ColorSwatch';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { Button } from '@/components/ui/button';
import { quickStartText } from '@/lib/quick-start-text';
import type { QuickStartSuggestion } from '@/types/timer';

interface QuickStartCardProps {
  suggestion: QuickStartSuggestion;
  today: LocalDate;
  onDismiss: () => void;
}

export function QuickStartCard({ suggestion, today, onDismiss }: QuickStartCardProps): JSX.Element {
  const actions = useTimerActions();
  const project = suggestion.project;
  const archived = project?.status === 'archived';
  return (
    <section aria-label="Quick start" className="card mb-6 flex flex-wrap items-center gap-3">
      {project ? <ColorSwatch hex={project.color} size={8} /> : <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-muted-foreground">{quickStartText(suggestion, today)}</p>
        {archived && project && <p className="mt-0.5 text-xs text-muted-foreground">{`${project.name} is archived, so this starts with No project.`}</p>}
      </div>
      <Button
        size="xs"
        onClick={() => actions.startWith({ projectId: suggestion.startProjectId, description: suggestion.description })}
        disabled={actions.pending}
      >
        Start
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Dismiss for today" onClick={onDismiss}>
        <X />
      </Button>
    </section>
  );
}
